import { useState, useEffect } from 'react';

// ── TOPBAR ────────────────────────────────────────────────────────────────────
export function Topbar({ title, subtitle, onBack, rightContent }) {
  return (
    <div style={{
      background: 'var(--accent-dark)', color: 'white',
      padding: '0 16px', height: 52,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'sticky', top: 0, zIndex: 100,
      boxShadow: '0 2px 8px rgba(0,0,0,.2)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {onBack && (
          <button onClick={onBack} style={{
            background: 'rgba(255,255,255,.15)', border: 'none', color: 'white',
            width: 32, height: 32, borderRadius: 8,
            fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>←</button>
        )}
        <div style={{ background: '#fff', borderRadius: 8, padding: '5px 10px', display: 'flex', alignItems: 'center', flex: 'none' }}>
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="OPEN Field Hub" style={{ height: 24, display: 'block' }} />
        </div>
        <div style={{ borderLeft: '1px solid rgba(255,255,255,.25)', paddingLeft: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, opacity: .7 }}>{subtitle}</div>}
        </div>
      </div>
      {rightContent}
    </div>
  );
}

// ── NAV TABS ─────────────────────────────────────────────────────────────────
export function NavTabs({ tabs, active, onChange }) {
  return (
    <div style={{
      background: 'white', borderBottom: '1px solid var(--border)',
      display: 'flex', overflowX: 'auto',
    }}>
      {tabs.map((tab, i) => (
        <button key={i} onClick={() => onChange(i)} style={{
          padding: '12px 18px', fontSize: 13,
          fontWeight: active === i ? 600 : 500,
          color: active === i ? 'var(--accent)' : 'var(--text-muted)',
          background: 'none', border: 'none',
          borderBottom: `2.5px solid ${active === i ? 'var(--accent)' : 'transparent'}`,
          whiteSpace: 'nowrap', transition: 'all .15s',
        }}>
          {tab}
        </button>
      ))}
    </div>
  );
}

// ── CARD ─────────────────────────────────────────────────────────────────────
export function Card({ children, style = {} }) {
  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 'var(--radius)',
      boxShadow: 'var(--shadow)', marginBottom: 12, overflow: 'hidden', ...style,
    }}>
      {children}
    </div>
  );
}

export function CardHeader({ title, right }) {
  return (
    <div style={{
      padding: '12px 16px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
      {right}
    </div>
  );
}

// ── STAT CHIP ────────────────────────────────────────────────────────────────
export function StatChip({ value, label, color = 'blue' }) {
  const colors = {
    blue:   { bg: 'var(--accent-light)', val: 'var(--accent)', border: '#BDD7EE' },
    green:  { bg: 'var(--green-bg)',     val: 'var(--green)',  border: '#A9D18E' },
    red:    { bg: 'var(--red-bg)',       val: 'var(--red)',    border: '#F4CCCC' },
    yellow: { bg: 'var(--yellow-bg)',    val: 'var(--yellow)', border: '#FFD966' },
    grey:   { bg: '#EDEDED',             val: '#555',          border: '#CCC'    },
  };
  const c = colors[color] || colors.blue;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      padding: '10px 14px', borderRadius: 8, flex: 1, minWidth: 70,
      background: c.bg, border: `1px solid ${c.border}`,
    }}>
      <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: c.val }}>{value}</span>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{label}</span>
    </div>
  );
}

// ── BUTTON ───────────────────────────────────────────────────────────────────
export function Btn({ children, onClick, color = 'primary', size = 'md', style = {}, disabled = false }) {
  const colors = {
    primary: { bg: 'var(--accent)',      fg: 'white' },
    danger:  { bg: '#C00000',            fg: 'white' },
    success: { bg: '#70AD47',            fg: 'white' },
    ghost:   { bg: 'var(--surface2)',    fg: 'var(--text-muted)', border: '1px solid var(--border)' },
    yellow:  { bg: '#F9A825',            fg: 'white' },
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 12 },
    md: { padding: '10px 18px', fontSize: 14 },
    lg: { padding: '13px 24px', fontSize: 15 },
    full: { padding: '13px', fontSize: 15, width: '100%' },
  };
  const c = colors[color] || colors.primary;
  const s = sizes[size] || sizes.md;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...s, background: c.bg, color: c.fg,
      border: c.border || 'none', borderRadius: 'var(--radius-sm)',
      fontWeight: 600, transition: 'opacity .15s',
      opacity: disabled ? .5 : 1,
      ...style,
    }}>
      {children}
    </button>
  );
}

// ── INPUT ────────────────────────────────────────────────────────────────────
export function Input({ label, value, onChange, type = 'text', placeholder, style = {} }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>{label}</label>}
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '10px 12px',
          border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
          fontSize: 14, outline: 'none', background: 'var(--surface2)',
          ...style,
        }}
      />
    </div>
  );
}

// ── SELECT ───────────────────────────────────────────────────────────────────
export function Select({ label, value, onChange, options, style = {} }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        width: '100%', padding: '10px 12px',
        border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
        fontSize: 14, outline: 'none', background: 'var(--surface2)',
        ...style,
      }}>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── SECTION TITLE ────────────────────────────────────────────────────────────
export function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '.06em',
      marginBottom: 8, marginTop: 4,
    }}>
      {children}
    </div>
  );
}

// ── SAVE BANNER ──────────────────────────────────────────────────────────────
export function SaveBanner({ message, show }) {
  return (
    <div style={{
      position: 'fixed', bottom: 20, left: '50%',
      transform: `translateX(-50%) translateY(${show ? 0 : 100}px)`,
      background: 'var(--accent-dark)', color: 'white',
      padding: '12px 24px', borderRadius: 30,
      fontWeight: 600, fontSize: 14,
      boxShadow: 'var(--shadow-md)',
      transition: 'transform .3s cubic-bezier(.34,1.56,.64,1)',
      zIndex: 300, whiteSpace: 'nowrap',
    }}>
      {message}
    </div>
  );
}

// ── STATUS BADGE ─────────────────────────────────────────────────────────────
export function StatusBadge({ code }) {
  const colors = {
    '1':      { bg: '#C6EFCE', fg: '#276221' },
    'СВ':     { bg: '#BDD7EE', fg: '#1F3864' },
    'ПЛ ВЫХ':{ bg: '#EDEDED', fg: '#555' },
    'БЛ-':    { bg: '#FFC7CE', fg: '#9C0006' },
    'ОТ-':    { bg: '#FFE699', fg: '#7D5A00' },
    'ДО-':    { bg: '#FCE4D6', fg: '#843C0C' },
    'ВК-':    { bg: '#E2EFDA', fg: '#375623' },
  };
  const c = colors[code] || { bg: '#D6BCF5', fg: '#44337A' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg,
    }}>
      {code}
    </span>
  );
}

// ── MODAL ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children }) {
  // Пока шторка открыта, жест «потянуть вниз» не должен обновлять страницу на телефоне
  useEffect(() => {
    if (!open) return undefined;
    const h = document.documentElement, b = document.body;
    const ph = h.style.overscrollBehaviorY, pb = b.style.overscrollBehaviorY;
    h.style.overscrollBehaviorY = 'none'; b.style.overscrollBehaviorY = 'none';
    return () => { h.style.overscrollBehaviorY = ph; b.style.overscrollBehaviorY = pb; };
  }, [open]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      overscrollBehavior: 'contain', touchAction: 'none',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: '18px 18px 0 0',
        width: '100%', maxWidth: 480, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        boxShadow: '0 -8px 40px rgba(0,0,0,.15)',
        animation: 'slideUp .22s ease-out', touchAction: 'pan-y', overscrollBehavior: 'contain',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 8px 20px', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', minWidth: 0, flex: 1 }}>{title}</div>
          <button onClick={onClose} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13, fontWeight: 700, color: 'var(--text)', background: '#EDF2F7', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>✕</span> Закрыть
          </button>
        </div>
        <div style={{ overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}

// ── LOADER ───────────────────────────────────────────────────────────────────
export function Loader({ text = 'Загрузка...' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 12 }}>
      <div style={{
        width: 32, height: 32, border: '3px solid var(--border)',
        borderTopColor: 'var(--accent)', borderRadius: '50%',
        animation: 'spin .8s linear infinite',
      }} />
      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{text}</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── FILE UPLOAD ──────────────────────────────────────────────────────────────
export function FileUpload({ label, accept, onFile, hint }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div>
      {label && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{label}</div>}
      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '24px 16px', borderRadius: 8, cursor: 'pointer',
        border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
        background: dragging ? 'var(--accent-light)' : 'var(--surface2)',
        transition: 'all .15s',
      }}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      >
        <span style={{ fontSize: 28, marginBottom: 8 }}>📂</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>Выбрать файл</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>или перетащите сюда</span>
        {hint && <span style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 6 }}>{hint}</span>}
        <input type="file" accept={accept} style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) onFile(f); }} />
      </label>
    </div>
  );
}
